<template>
  <div class="search__container row-span-2">
    <ContainerGrid>
      <MainColumn>
        <div class="search__content">
          <div>
            <div class="search__content-area">
              <p class="type-heading-sm mb-sm text-text-brand">
                Sök anställda och arbetsgivare
              </p>
              <p>
                Ange ett giltigt personnummer, organisationsnummer eller
                ärende-id
              </p>
            </div>
            <div>
              <SearchBar :errorMessage="state.error" @search-text-change="onSearchTextChange" />
            </div>
            <div class="search__result-area" v-if="showResultView">
              <p v-if="anstalldLink && hasForetag">
                {{ individInfoText }} är både anställd och
                arbetsgivarrepresentant.
              </p>
              <p><strong>Vilken vy vill du se</strong></p>
              <div class="search__result-buttons">
                <avtalat-button v-if="anstalldLink" v-on:click="goToAnstalld()" outlined>
                  Visa individ
                </avtalat-button>
                <avtalat-button v-if="hasForetag" v-on:click="showForetag()" outlined>
                  Visa företag
                </avtalat-button>
              </div>
              <div v-if="state.showEmployers">
                <div class="search__result-info">
                  <p>Arbetsgivarrepresentant för flera arbetsgivare</p>
                  <p><strong>Vilken arbetsgivare vill du se?</strong></p>
                </div>
                <ForetagList :foretag-list="state.searchResult?.data?.filter(
                  (r) => r.responseType === SearchResponseType.Foretag,
                ) || []
                  " />
              </div>
            </div>
          </div>
        </div>
        <avtalat-spinner class="search__searching-spinner" v-if="!state.searchResolved" asOverlay="black"
          text="Söker anställda och arbetsgivare..." />
        <avtalat-spinner class="search__searching-spinner" v-if="state.isCreatingKundkontakt" asOverlay="black"
          text="Öppnar Kundkontakt i CRM..." />
      </MainColumn>
      <SidebarColumn class="overflow-hidden">
        <RightSideBar />
      </SidebarColumn>
    </ContainerGrid>
  </div>
  <div class="search__no-search-result" v-if="shouldShowNoSearchResult">
    <avtalat-modal :closeOnClickOutside="true" :closeOnEsc="true" :closeFn="onCloseNoSearchResultModal"
      :idealHeight="400">
      <template v-slot:default>
        <div class="search__no-search-result-modal-content">
          <avtalat-icon id="kryss" :size="60" color="feedback-alert-darker" />
          <h2 class="type-heading-lg">Inga träffar</h2>
          <div>
            <p>
              Din sökning på
              <strong>{{
                state.searchResult?.query || state.notFoundNumber
              }}</strong>
              gav inga träffar.
            </p>
            <p>
              Vänligen kontrollera så att du har angivit ett giltigt
              personnummer, organisationsnummer eller ärende-id.
            </p>
          </div>
          <avtalat-button @click="onCloseNoSearchResultModal()">Stäng</avtalat-button>
        </div>
      </template>
    </avtalat-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue";

import SearchBar from "@/components/SearchBar.vue";
import {
  SearchResponse,
  SearchResponseType,
  ValidationProblemDetails,
} from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { ContainerGrid, MainColumn, SidebarColumn } from "@/layout";
import { personnummerWithDash12Digits } from "@/common/formatters";
import { isCrmArendeNumber, isFreshdeskTicketId } from "@/common/validators";

import ForetagList from "./ForetagList.vue";
import RightSideBar from "./RightSideBar.vue";

type SearchResult = {
  query: string;
  data: SearchResponse[] | null;
};

const apiClient = useApi();
const navigation = useNavigation();
const state = reactive<{
  searchResolved: boolean;
  searchResult: SearchResult | null;
  notFoundNumber: string | null;
  error?: string;
  showEmployers: boolean;
  manuallyClosedNoSearchModal: boolean;
  canCreateKundkontakt: boolean;
  isCreatingKundkontakt: boolean;
}>({
  searchResolved: true,
  searchResult: null,
  notFoundNumber: navigation.currentRoute.query.notfound as string,
  error: undefined,
  showEmployers: false,
  manuallyClosedNoSearchModal: false,
  canCreateKundkontakt: false,
  isCreatingKundkontakt: false,
});

watch(
  () => navigation.currentRoute.query.q,
  (q, oldQ) => {
    if (q !== oldQ) search(q as string);
  },
  { immediate: true }
);

function goToAnstalld() {
  const personResponse = state.searchResult?.data?.filter(
    (r) => r.responseType === SearchResponseType.Individ,
  )[0];

  if (personResponse?.id) {
    navigation.openAnstalld(personResponse.id);
  }
}

function showForetag() {
  const foretagResponses =
    state.searchResult?.data?.filter(
      (r) => r.responseType === SearchResponseType.Foretag,
    ) ?? [];
  if (foretagResponses.length > 1) {
    state.showEmployers = true;
  } else {
    gotoArbetsgivare();
  }
}

function gotoArbetsgivare() {
  const foretagResponse = state.searchResult?.data?.filter(
    (r) => r.responseType === SearchResponseType.Foretag,
  )[0];
  if (!foretagResponse?.idNumber) {
    return;
  }

  navigation.openArbetsgivare(foretagResponse.idNumber);
}

async function search(query: string) {
  state.searchResolved = false;
  state.error = undefined;
  state.searchResult = null;
  state.manuallyClosedNoSearchModal = false;

  try {
    const searchResponse = await apiClient.search(query);
    state.searchResolved = true;
    state.searchResult = { query, data: searchResponse };

    if (searchResponse.length !== 1) {
      return;
    }

    if (
      searchResponse[0].responseType === SearchResponseType.Individ &&
      searchResponse[0].id
    ) {
      const ticketId = isFreshdeskTicketId(query) ? query : undefined;
      navigation.openAnstalld(searchResponse[0].id, ticketId);

      return;
    }
    if (
      searchResponse[0].responseType === SearchResponseType.Foretag &&
      searchResponse[0].idNumber
    ) {
      const arendeNumber = isCrmArendeNumber(query) ? query : undefined;
      navigation.openArbetsgivare(searchResponse[0].idNumber, arendeNumber);
      return;
    }
  } catch (e) {
    if (e instanceof ValidationProblemDetails) {
      if (e.status === 400) {
        state.error = "Ogiltigt person- eller organisationsnummer";
      }
    }

    state.searchResolved = true;
    state.searchResult = null;
  }
}

const showResultView = computed(
  () =>
    state.searchResolved &&
    state.error == null &&
    state.searchResult?.data &&
    state.searchResult.data.length > 0,
);

const anstalldLink = computed(() => {
  const result =
    state.searchResolved &&
    state.error == null &&
    state.searchResult?.data &&
    state.searchResult?.data?.filter(
      (r) => r.responseType === SearchResponseType.Individ,
    ).length > 0;
  return result;
});

const individInfoText = computed(() => {
  const individ = state.searchResult?.data?.find(
    (x) => x.responseType === SearchResponseType.Individ,
  );
  return [individ?.name, personnummerWithDash12Digits(individ?.idNumber)]
    .filter((x) => !!x)
    .join(" ");
});

const hasForetag = computed(() => {
  const result =
    state.searchResolved &&
    state.error == null &&
    state.searchResult?.data &&
    state.searchResult?.data.filter(
      (r) => r.responseType === SearchResponseType.Foretag,
    ).length > 0;
  return result;
});

const shouldShowNoSearchResult = computed(() => {
  const hasNoResult =
    (state.searchResolved && state.searchResult?.data?.length === 0) ||
    state.notFoundNumber;
  return hasNoResult && !state.manuallyClosedNoSearchModal;
});

const onCloseNoSearchResultModal = () => {
  state.manuallyClosedNoSearchModal = true;
  state.notFoundNumber = null;
};

const onSearchTextChange = () => {
  state.error = undefined;
};

onMounted(() => {
  navigation.clearNotFound();
  state.canCreateKundkontakt = true;
});
</script>

<style>
.search__container {
  display: flex;
  flex-grow: 1;
}

.search__content {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 50px auto;
}

.search__content-area {
  margin-bottom: 50px;
  text-align: center;
}

.search__result-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.search__result-info {
  margin-bottom: 20px;
  text-align: center;
}

.search__result-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1em;
  margin: 1em 0;
}

.search__no-search-result-modal-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  padding: 1em;
  height: 100%;
  text-align: center;
}

.search__searching-spinner {
  z-index: 1;
}
</style>
